import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import { toast } from 'react-toastify';

// Types
interface CompetitorProfileData {
  _id: string;
  firstName: string;
  lastName: string;
  profilePicture?: string;
  areasOfInterest: {
    mainCategory: string;
    subCategory: string;
    niche: string;
  }[];
  bio?: string;
  biography?: string;
  strengths?: string[];
  likes?: string[];
  hobbies?: string[];
  cbcCompetencies?: string[];
  skills?: string[];
  projects?: {
    name: string;
    description?: string;
    url?: string;
    date?: Date;
    competenciesCovered?: string[];
  }[];
  extracurricularActivities?: string[];
  interests?: string[];
  careerAspiration?: string;
  competitionPreferences?: {
    preferredStakeRange: 'low' | 'medium' | 'high' | 'any';
    preferredCategories: string[];
    timeZone: string;
  };
  // Add educational information
  educationalInfo?: {
    isCurrentlyStudying: boolean;
    level: 'primary_school' | 'form_4_leaver' | 'tvet' | 'college' | 'university' | 'postgraduate' | 'not_studying';
    institution?: string;
    courseOfStudy?: string;
    yearOfStudy?: number;
    expectedGraduationYear?: number;
  };
  // Add location information
  personalInfo?: {
    age: number;
    gender: 'male' | 'female' | 'other' | 'prefer_not_to_say';
    region: string;
    province: string;
    city?: string;
    country: string;
    timezone: string;
  };
  isOnline?: boolean;
  lastSeen?: string;
  rating?: number;
  wins?: number;
  totalMatches?: number;
}

interface CompetitorExtended {
  id: string;
  name: string;
  balance: number;
  wins: number;
  rating: number;
  status: string;
  isOnline: boolean;
  avatar: string;
  accountType: string;
  gradeLevel?: number;
  university?: string;
  totalMatches: number;
  lastSeen?: string;
  // Add educational and location information
  educationalInfo?: {
    isCurrentlyStudying: boolean;
    level: 'primary_school' | 'form_4_leaver' | 'tvet' | 'college' | 'university' | 'postgraduate' | 'not_studying';
    institution?: string;
    courseOfStudy?: string;
    yearOfStudy?: number;
    expectedGraduationYear?: number;
  };
  personalInfo?: {
    age: number;
    gender: 'male' | 'female' | 'other' | 'prefer_not_to_say';
    region: string;
    province: string;
    city?: string;
    country: string;
    timezone: string;
  };
}

interface ViewProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  competitor: CompetitorExtended;
  onSendChallengeRequest?: (competitor: CompetitorExtended) => void;
}

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 20px;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 30px;
  border-radius: 20px 20px 0 0;
  text-align: center;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 18px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }
`;

const ProfileSection = styled.div`
  padding: 25px;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    border-bottom: none;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: #2f3542;
  margin: 0 0 15px 0;
  display: flex;
  align-items: center;
  gap: 10px;
  
  &::before {
    content: '';
    width: 4px;
    height: 20px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 2px;
  }
`;

const ProfileGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 15px;
`;

const ProfileItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const Label = styled.span`
  font-size: 0.8rem;
  color: #666;
  font-weight: 500;
`;

const Value = styled.span`
  font-size: 1rem;
  color: #2f3542;
  font-weight: 600;
`;

const StatusBadge = styled.span<{ isOnline: boolean }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  background: ${props => props.isOnline ? '#e8f5e8' : '#f5f5f5'};
  color: ${props => props.isOnline ? '#2e7d32' : '#666'};
  border: 1px solid ${props => props.isOnline ? '#c8e6c9' : '#e0e0e0'};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
  margin-top: 15px;
`;

const StatCard = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 15px;
  text-align: center;
  border: 1px solid #e0e0e0;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #667eea;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  font-size: 0.8rem;
  color: #666;
  font-weight: 500;
`;

const ProfilePicture = styled.div<{ imageUrl?: string }>`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: ${props => props.imageUrl 
    ? `url(${props.imageUrl}) center/cover` 
    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
  };
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  color: white;
  margin: 0 auto 20px;
  border: 4px solid white;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
`;

const Tag = styled.span`
  background: #f3f4f6;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  color: #4b5563;
  border: 1px solid #e5e7eb;
`;

const ChallengeRequestButton = styled.button`
  background: linear-gradient(45deg, #4caf50, #8bc34a);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  margin-top: 15px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(76, 175, 80, 0.3);
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid #ffffff;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s linear infinite;
  margin-right: 8px;
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ViewProfileModal: React.FC<ViewProfileModalProps> = ({ isOpen, onClose, competitor, onSendChallengeRequest }) => {
  const { user: authUser, token } = useAuth();
  const [profileData, setProfileData] = useState<CompetitorProfileData | null>(null);
  const [loading, setLoading] = useState(false);
  const [sendingChallenge, setSendingChallenge] = useState(false);

  // Load detailed profile data when modal opens
  useEffect(() => {
    if (isOpen && competitor.id && token) {
      loadCompetitorProfile();
    }
  }, [isOpen, competitor.id, token]);

  const loadCompetitorProfile = async () => {
    try {
      setLoading(true);
      
      const response = await api.get(`/users/competitor-profile/${competitor.id}`);
      
      if (response.data?.success) {
        setProfileData(response.data.data);
      }
    } catch (error) {
      console.error('Error loading competitor profile:', error);
      toast.error('Failed to load competitor profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSendChallengeRequest = async () => {
    if (!authUser || !token) {
      toast.error('Please log in to send challenge requests');
      return;
    }

    // If parent component provides a handler, use it
    if (onSendChallengeRequest) {
      onSendChallengeRequest(competitor);
      return;
    }

    // Fallback to old implementation
    try {
      setSendingChallenge(true);
      
      const challengeData = {
        opponentId: competitor.id,
        area: {
          subject: 'General Knowledge',
          topic: 'Mixed Topics'
        },
        mode: 'live',
        stake: 100,
        message: `Challenge request from ${authUser.firstName || 'A competitor'}`
      };

      const response = await api.post('/competitors/challenge-request', challengeData);
      
      if (response.data?.success) {
        toast.success('Challenge request sent successfully! 🎯');
      } else {
        throw new Error('Failed to send challenge request');
      }
    } catch (error) {
      console.error('Error sending challenge request:', error);
      toast.error('Failed to send challenge request. Please try again.');
    } finally {
      setSendingChallenge(false);
    }
  };

  if (!isOpen) return null;

  const formatBalance = (balance: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(balance);
  };

  const getAccountTypeDisplay = () => {
    if (competitor.accountType === 'student') {
      return `Student - Grade ${competitor.gradeLevel}`;
    } else if (competitor.accountType === 'university') {
      return `University Student - ${competitor.university}`;
    }
    return 'Public User';
  };

  const getAvailabilityStatus = () => {
    if (competitor.isOnline) {
      return {
        text: '🟢 Available for new challenges',
        color: '#4caf50',
        background: '#e8f5e8'
      };
    } else {
      return {
        text: '🔴 Currently offline for new challenges',
        color: '#f44336',
        background: '#ffebee'
      };
    }
  };

  const availabilityStatus = getAvailabilityStatus();

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <CloseButton onClick={onClose}>×</CloseButton>
          <h1 style={{ margin: 0, fontSize: '2rem', fontWeight: '700' }}>
            {competitor.name}'s Profile
          </h1>
          <p style={{ margin: '10px 0 0 0', opacity: 0.9 }}>
            View competitor details and send challenge requests
          </p>
        </ModalHeader>

        {/* Profile Picture Section */}
        <ProfileSection>
          <SectionTitle>📸 Profile Picture</SectionTitle>
          <div style={{ textAlign: 'center' }}>
            <ProfilePicture imageUrl={profileData?.profilePicture}>
              {!profileData?.profilePicture && '👤'}
            </ProfilePicture>
          </div>
        </ProfileSection>

        {/* Basic Information */}
        <ProfileSection>
          <SectionTitle>👤 Basic Information</SectionTitle>
          <ProfileGrid>
            <ProfileItem>
              <Label>Full Name</Label>
              <Value>{competitor.name}</Value>
            </ProfileItem>
            <ProfileItem>
              <Label>Account Type</Label>
              <Value>{getAccountTypeDisplay()}</Value>
            </ProfileItem>
            <ProfileItem>
              <Label>Status</Label>
              <StatusBadge isOnline={competitor.isOnline}>
                {/* Status indicator only - no text */}
              </StatusBadge>
            </ProfileItem>
            <ProfileItem>
              <Label>Last Seen</Label>
              <Value>{competitor.lastSeen || 'Unknown'}</Value>
            </ProfileItem>
          </ProfileGrid>
        </ProfileSection>

        {/* Competitor Introduction */}
        <ProfileSection>
          <SectionTitle>👋 Introduction</SectionTitle>
          <div style={{ 
            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', 
            borderRadius: '12px', 
            padding: '25px',
            lineHeight: '1.6',
            color: '#2f3542',
            border: '1px solid #dee2e6',
            textAlign: 'center'
          }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <div style={{ 
                  fontSize: '1.1rem', 
                  color: '#667eea',
                  marginBottom: '10px'
                }}>
                  🔄 Loading profile information...
                </div>
                <div style={{ 
                  width: '40px', 
                  height: '4px', 
                  background: 'linear-gradient(90deg, #667eea, #764ba2)', 
                  borderRadius: '2px',
                  margin: '0 auto',
                  animation: 'loading 1.5s ease-in-out infinite'
                }}></div>
                <style>{`
                  @keyframes loading {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(100%); }
                  }
                `}</style>
              </div>
            ) : (
              <>
                {/* Educational Information */}
                {(profileData?.educationalInfo || competitor.educationalInfo) ? (
                  <div style={{ marginBottom: '15px' }}>
                    {(() => {
                      const eduInfo = profileData?.educationalInfo || competitor.educationalInfo;
                      if (eduInfo?.isCurrentlyStudying && eduInfo.institution && eduInfo.courseOfStudy && eduInfo.yearOfStudy) {
                        // Format the year of study
                        const yearText = eduInfo.yearOfStudy === 1 ? '1st' : 
                                        eduInfo.yearOfStudy === 2 ? '2nd' : 
                                        eduInfo.yearOfStudy === 3 ? '3rd' : 
                                        eduInfo.yearOfStudy + 'th';
                        
                        // Format the course of study (capitalize first letter)
                        const courseText = eduInfo.courseOfStudy.charAt(0).toUpperCase() + 
                                         eduInfo.courseOfStudy.slice(1).toLowerCase();
                        
                        return (
                          <p style={{ 
                            fontSize: '1.1rem', 
                            fontWeight: '600', 
                            color: '#667eea',
                            margin: '0 0 10px 0'
                          }}>
                            A {yearText} Year {courseText} at {eduInfo.institution}.
                          </p>
                        );
                      } else if (eduInfo?.institution && eduInfo.courseOfStudy) {
                        // If no year of study but has institution and course
                        const courseText = eduInfo.courseOfStudy.charAt(0).toUpperCase() + 
                                         eduInfo.courseOfStudy.slice(1).toLowerCase();
                        return (
                          <p style={{ 
                            fontSize: '1.1rem', 
                            fontWeight: '600', 
                            color: '#667eea',
                            margin: '0 0 10px 0'
                          }}>
                            A {courseText} at {eduInfo.institution}.
                          </p>
                        );
                      } else if (eduInfo?.institution) {
                        // If only institution is available
                        return (
                          <p style={{ 
                            fontSize: '1.1rem', 
                            fontWeight: '600', 
                            color: '#667eea',
                            margin: '0 0 10px 0'
                          }}>
                            Studying at {eduInfo.institution}.
                          </p>
                        );
                      }
                      return (
                        <p style={{ 
                          fontSize: '1rem', 
                          color: '#6c757d',
                          margin: '0 0 10px 0',
                          fontStyle: 'italic'
                        }}>
                          Educational information not available
                        </p>
                      );
                    })()}
                  </div>
                ) : (
                  <div style={{ marginBottom: '15px' }}>
                    <p style={{ 
                      fontSize: '1rem', 
                      color: '#6c757d',
                      margin: '0 0 10px 0',
                      fontStyle: 'italic'
                    }}>
                      Educational information not available
                    </p>
                  </div>
                )}
                
                {/* Location Information */}
                {(profileData?.personalInfo || competitor.personalInfo) ? (
                  <div>
                    {(() => {
                      const personalInfo = profileData?.personalInfo || competitor.personalInfo;
                      if (personalInfo) {
                        // Prioritize city, then province, then region, then country
                        const location = personalInfo.city || personalInfo.province || personalInfo.region || personalInfo.country;
                        if (location) {
                          return (
                            <p style={{ 
                              fontSize: '1rem', 
                              color: '#495057',
                              margin: '0',
                              fontStyle: 'italic'
                            }}>
                              Comes from: {location}
                            </p>
                          );
                        }
                      }
                      return (
                        <p style={{ 
                          fontSize: '1rem', 
                          color: '#6c757d',
                          margin: '0',
                          fontStyle: 'italic'
                        }}>
                          Location information not available
                        </p>
                        );
                    })()}
                  </div>
                ) : (
                  <div>
                    <p style={{ 
                      fontSize: '1rem', 
                      color: '#6c757d',
                      margin: '0',
                      fontStyle: 'italic'
                    }}>
                      Location information not available
                    </p>
                  </div>
                )}
                
                
              </>
            )}
          </div>
        </ProfileSection>

        {/* Educational Summary */}
        {(profileData?.educationalInfo || competitor.educationalInfo) && (
          <ProfileSection>
            <SectionTitle>🎓 Educational Summary</SectionTitle>
            <div style={{ 
              background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)', 
              borderRadius: '12px', 
              padding: '20px',
              lineHeight: '1.6',
              color: '#2f3542',
              border: '1px solid #4caf50',
              textAlign: 'center'
            }}>
              {(() => {
                const eduInfo = profileData?.educationalInfo || competitor.educationalInfo;
                if (!eduInfo) return null;
                
                if (eduInfo.isCurrentlyStudying && eduInfo.institution && eduInfo.courseOfStudy && eduInfo.yearOfStudy) {
                  const yearText = eduInfo.yearOfStudy === 1 ? '1st' : 
                                  eduInfo.yearOfStudy === 2 ? '2nd' : 
                                  eduInfo.yearOfStudy === 3 ? '3rd' : 
                                  eduInfo.yearOfStudy + 'th';
                  
                  const courseText = eduInfo.courseOfStudy.charAt(0).toUpperCase() + 
                                   eduInfo.courseOfStudy.slice(1).toLowerCase();
                  
                  return (
                    <div>
                      <p style={{ 
                        fontSize: '1.3rem', 
                        fontWeight: '700', 
                        color: '#2e7d32',
                        margin: '0 0 15px 0'
                      }}>
                        🎯 Currently Studying
                      </p>
                      <p style={{ 
                        fontSize: '1.2rem', 
                        fontWeight: '600', 
                        color: '#388e3c',
                        margin: '0 0 10px 0'
                      }}>
                        {yearText} Year {courseText}
                      </p>
                      <p style={{ 
                        fontSize: '1.1rem', 
                        color: '#4caf50',
                        margin: '0',
                        fontStyle: 'italic'
                      }}>
                        at {eduInfo.institution}
                      </p>
                    </div>
                  );
                } else if (eduInfo.institution && eduInfo.courseOfStudy) {
                  const courseText = eduInfo.courseOfStudy.charAt(0).toUpperCase() + 
                                   eduInfo.courseOfStudy.slice(1).toLowerCase();
                  
                  return (
                    <div>
                      <p style={{ 
                        fontSize: '1.3rem', 
                        fontWeight: '700', 
                        color: '#2e7d32',
                        margin: '0 0 15px 0'
                      }}>
                        🎯 Educational Background
                      </p>
                      <p style={{ 
                        fontSize: '1.2rem', 
                        fontWeight: '600', 
                        color: '#388e3c',
                        margin: '0 0 10px 0'
                      }}>
                        {courseText}
                      </p>
                      <p style={{ 
                        fontSize: '1.1rem', 
                        color: '#4caf50',
                        margin: '0',
                        fontStyle: 'italic'
                      }}>
                        at {eduInfo.institution}
                      </p>
                    </div>
                  );
                } else if (eduInfo.institution) {
                  return (
                    <div>
                      <p style={{ 
                        fontSize: '1.3rem', 
                        fontWeight: '700', 
                        color: '#2e7d32',
                        margin: '0 0 15px 0'
                      }}>
                        🎯 Institution
                      </p>
                      <p style={{ 
                        fontSize: '1.2rem', 
                        fontWeight: '600', 
                        color: '#388e3c',
                        margin: '0'
                      }}>
                        {eduInfo.institution}
                      </p>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          </ProfileSection>
        )}

        {/* Location Summary */}
        {(profileData?.personalInfo || competitor.personalInfo) && (
          <ProfileSection>
            <SectionTitle>📍 Location Summary</SectionTitle>
            <div style={{ 
              background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)', 
              borderRadius: '12px', 
              padding: '20px',
              lineHeight: '1.6',
              color: '#2f3542',
              border: '1px solid #2196f3',
              textAlign: 'center'
            }}>
              {(() => {
                const personalInfo = profileData?.personalInfo || competitor.personalInfo;
                if (!personalInfo) return null;
                
                const location = personalInfo.city || personalInfo.province || personalInfo.region || personalInfo.country;
                if (location) {
                  return (
                    <div>
                      <p style={{ 
                        fontSize: '1.3rem', 
                        fontWeight: '700', 
                        color: '#1565c0',
                        margin: '0 0 15px 0'
                      }}>
                        🌍 Location
                      </p>
                      <p style={{ 
                        fontSize: '1.2rem', 
                        fontWeight: '600', 
                        color: '#1976d2',
                        margin: '0 0 10px 0'
                      }}>
                        {location}
                      </p>
                      <div style={{ 
                        fontSize: '0.9rem', 
                        color: '#42a5f5',
                        background: 'rgba(255, 255, 255, 0.7)',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        marginTop: '10px',
                        display: 'inline-block'
                      }}>
                        {personalInfo.city && <span>City: {personalInfo.city}</span>}
                        {personalInfo.city && personalInfo.province && <span> • </span>}
                        {personalInfo.province && <span>Province: {personalInfo.province}</span>}
                        {personalInfo.province && personalInfo.region && <span> • </span>}
                        {personalInfo.region && <span>Region: {personalInfo.region}</span>}
                        {(personalInfo.city || personalInfo.province || personalInfo.region) && personalInfo.country && <span> • </span>}
                        {personalInfo.country && <span>Country: {personalInfo.country}</span>}
                      </div>
                    </div>
                  );
                }
                return (
                  <div>
                    <p style={{ 
                      fontSize: '1.3rem', 
                      fontWeight: '700', 
                      color: '#1565c0',
                      margin: '0 0 15px 0'
                    }}>
                      🌍 Location
                    </p>
                    <p style={{ 
                      fontSize: '1rem', 
                      color: '#6c757d',
                      margin: '0',
                      fontStyle: 'italic'
                    }}>
                      Location information not available
                    </p>
                  </div>
                );
              })()}
            </div>
          </ProfileSection>
        )}

        {/* Competition Statistics */}
        <ProfileSection>
          <SectionTitle>📊 Competition Statistics</SectionTitle>
          <StatsGrid>
            <StatCard>
              <StatValue>{competitor.rating}</StatValue>
              <StatLabel>Rating</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{competitor.wins}</StatValue>
              <StatLabel>Wins</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{competitor.totalMatches}</StatValue>
              <StatLabel>Total Matches</StatLabel>
            </StatCard>
          </StatsGrid>
          <div style={{ marginTop: '15px', textAlign: 'center' }}>
            <div style={{ 
              fontSize: '0.9rem', 
              color: '#666',
              background: '#f8f9fa',
              padding: '10px',
              borderRadius: '8px',
              border: '1px solid #e0e0e0'
            }}>
              Win Rate: {competitor.totalMatches > 0 ? Math.round((competitor.wins / competitor.totalMatches) * 100) : 0}%
            </div>
          </div>
        </ProfileSection>

        {/* Financial Information */}
        <ProfileSection>
          <SectionTitle>💰 Financial Information</SectionTitle>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              fontSize: '2rem', 
              fontWeight: '700', 
              color: '#667eea',
              marginBottom: '10px'
            }}>
              {formatBalance(competitor.balance)}
            </div>
            <div style={{ fontSize: '0.9rem', color: '#666' }}>
              Available Balance for Competitions
            </div>
          </div>
        </ProfileSection>

        {/* Areas of Interest */}
        {profileData?.areasOfInterest && profileData.areasOfInterest.length > 0 && (
          <ProfileSection>
            <SectionTitle>🎯 Areas of Interest</SectionTitle>
            <div style={{ background: '#f8f9fa', borderRadius: '12px', padding: '20px' }}>
              {profileData.areasOfInterest.map((interest, index) => (
                <div key={index} style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '15px',
                  marginBottom: '15px',
                  border: '1px solid #e0e0e0',
                  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
                }}>
                  <div style={{ fontWeight: '600', color: '#2f3542' }}>
                    {interest.mainCategory} → {interest.subCategory} → {interest.niche}
                  </div>
                </div>
              ))}
            </div>
          </ProfileSection>
        )}

        {/* Bio */}
        {(profileData?.bio || profileData?.biography) && (
          <ProfileSection>
            <SectionTitle>📝 About</SectionTitle>
            <div style={{ 
              background: '#f8f9fa', 
              borderRadius: '12px', 
              padding: '20px',
              lineHeight: '1.6',
              color: '#2f3542'
            }}>
              {profileData.bio || profileData.biography}
            </div>
          </ProfileSection>
        )}

        {/* Skills and Strengths */}
        {(profileData?.strengths && profileData.strengths.length > 0) && (
          <ProfileSection>
            <SectionTitle>💪 Strengths</SectionTitle>
            <TagList>
              {profileData.strengths.map((strength, index) => (
                <Tag key={index}>{strength}</Tag>
              ))}
            </TagList>
          </ProfileSection>
        )}

        {/* Skills */}
        {(profileData?.skills && profileData.skills.length > 0) && (
          <ProfileSection>
            <SectionTitle>🛠️ Skills</SectionTitle>
            <TagList>
              {profileData.skills.map((skill, index) => (
                <Tag key={index}>{skill}</Tag>
              ))}
            </TagList>
          </ProfileSection>
        )}

        {/* Hobbies and Interests */}
        {(profileData?.hobbies && profileData.hobbies.length > 0) && (
          <ProfileSection>
            <SectionTitle>🎨 Hobbies & Interests</SectionTitle>
            <TagList>
              {profileData.hobbies.map((hobby, index) => (
                <Tag key={index}>{hobby}</Tag>
              ))}
            </TagList>
          </ProfileSection>
        )}

        {/* Educational Information */}
        {(profileData?.educationalInfo || competitor.educationalInfo) && (
          <ProfileSection>
            <SectionTitle>📚 Educational Information</SectionTitle>
            <div style={{ 
              background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)', 
              borderRadius: '12px', 
              padding: '20px',
              lineHeight: '1.6',
              color: '#2f3542',
              border: '1px solid #ce93d8'
            }}>
              {(() => {
                const eduInfo = profileData?.educationalInfo || competitor.educationalInfo;
                if (!eduInfo) return null;
                
                return (
                  <>
                    <div style={{ 
                      background: 'rgba(255, 255, 255, 0.7)', 
                      borderRadius: '8px', 
                      padding: '15px',
                      marginBottom: '15px'
                    }}>
                      <p style={{ 
                        fontSize: '1.1rem', 
                        fontWeight: '600', 
                        color: '#7b1fa2',
                        margin: '0 0 10px 0',
                        textAlign: 'center'
                      }}>
                        🎓 {eduInfo.isCurrentlyStudying ? 'Currently Studying' : 'Education Status'}
                      </p>
                      <p style={{ 
                        fontSize: '1rem', 
                        color: '#4a148c',
                        margin: '0',
                        textAlign: 'center'
                      }}>
                        {eduInfo.isCurrentlyStudying ? 'Yes' : 'No'}
                      </p>
                    </div>
                    
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                      gap: '15px' 
                    }}>
                      {eduInfo.level && (
                        <div style={{ 
                          background: 'rgba(255, 255, 255, 0.7)', 
                          borderRadius: '8px', 
                          padding: '12px',
                          textAlign: 'center'
                        }}>
                          <p style={{ margin: '0 0 5px 0', fontSize: '0.9rem', color: '#666' }}>
                            <strong>Level</strong>
                          </p>
                          <p style={{ margin: '0', fontSize: '1rem', color: '#4a148c', fontWeight: '600' }}>
                            {eduInfo.level.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </p>
                        </div>
                      )}
                      
                      {eduInfo.institution && (
                        <div style={{ 
                          background: 'rgba(255, 255, 255, 0.7)', 
                          borderRadius: '8px', 
                          padding: '12px',
                          textAlign: 'center'
                        }}>
                          <p style={{ margin: '0 0 5px 0', fontSize: '0.9rem', color: '#666' }}>
                            <strong>Institution</strong>
                          </p>
                          <p style={{ margin: '0', fontSize: '1rem', color: '#4a148c', fontWeight: '600' }}>
                            {eduInfo.institution}
                          </p>
                        </div>
                      )}
                      
                      {eduInfo.courseOfStudy && (
                        <div style={{ 
                          background: 'rgba(255, 255, 255, 0.7)', 
                          borderRadius: '8px', 
                          padding: '12px',
                          textAlign: 'center'
                        }}>
                          <p style={{ margin: '0 0 5px 0', fontSize: '0.9rem', color: '#666' }}>
                            <strong>Course of Study</strong>
                          </p>
                          <p style={{ margin: '0', fontSize: '1rem', color: '#4a148c', fontWeight: '600' }}>
                            {eduInfo.courseOfStudy}
                          </p>
                        </div>
                      )}
                      
                      {eduInfo.yearOfStudy && (
                        <div style={{ 
                          background: 'rgba(255, 255, 255, 0.7)', 
                          borderRadius: '8px', 
                          padding: '12px',
                          textAlign: 'center'
                        }}>
                          <p style={{ margin: '0 0 5px 0', fontSize: '0.9rem', color: '#666' }}>
                            <strong>Year of Study</strong>
                          </p>
                          <p style={{ margin: '0', fontSize: '1rem', color: '#4a148c', fontWeight: '600' }}>
                            {eduInfo.yearOfStudy === 1 ? '1st' : 
                             eduInfo.yearOfStudy === 2 ? '2nd' : 
                             eduInfo.yearOfStudy === 3 ? '3rd' : 
                             eduInfo.yearOfStudy + 'th'} Year
                          </p>
                        </div>
                      )}
                      
                      {eduInfo.expectedGraduationYear && (
                        <div style={{ 
                          background: 'rgba(255, 255, 255, 0.7)', 
                          borderRadius: '8px', 
                          padding: '12px',
                          textAlign: 'center'
                        }}>
                          <p style={{ margin: '0 0 5px 0', fontSize: '0.9rem', color: '#666' }}>
                            <strong>Expected Graduation</strong>
                          </p>
                          <p style={{ margin: '0', fontSize: '1rem', color: '#4a148c', fontWeight: '600' }}>
                            {eduInfo.expectedGraduationYear}
                          </p>
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          </ProfileSection>
        )}

        {/* Location Information */}
        {(profileData?.personalInfo || competitor.personalInfo) && (
          <ProfileSection>
            <SectionTitle>📍 Location</SectionTitle>
            <div style={{ 
              background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)', 
              borderRadius: '12px', 
              padding: '20px',
              lineHeight: '1.6',
              color: '#2f3542',
              border: '1px solid #90caf9',
              textAlign: 'center'
            }}>
              {(() => {
                const personalInfo = profileData?.personalInfo || competitor.personalInfo;
                if (!personalInfo) return null;
                
                const location = personalInfo.city || personalInfo.province || personalInfo.region || personalInfo.country;
                if (location) {
                  return (
                    <div>
                      <p style={{ 
                        fontSize: '1.2rem', 
                        fontWeight: '600', 
                        color: '#1976d2',
                        margin: '0 0 10px 0'
                      }}>
                        🌍 Comes from: {location}
                      </p>
                      <div style={{ 
                        fontSize: '0.9rem', 
                        color: '#666',
                        background: 'rgba(255, 255, 255, 0.7)',
                        padding: '10px',
                        borderRadius: '8px',
                        marginTop: '10px'
                      }}>
                        {personalInfo.city && <span>City: {personalInfo.city}</span>}
                        {personalInfo.city && personalInfo.province && <span> • </span>}
                        {personalInfo.province && <span>Province: {personalInfo.province}</span>}
                        {personalInfo.province && personalInfo.region && <span> • </span>}
                        {personalInfo.region && <span>Region: {personalInfo.region}</span>}
                        {(personalInfo.city || personalInfo.province || personalInfo.region) && personalInfo.country && <span> • </span>}
                        {personalInfo.country && <span>Country: {personalInfo.country}</span>}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          </ProfileSection>
        )}

        {/* Personal Information */}
        {(profileData?.personalInfo || competitor.personalInfo) && (
          <ProfileSection>
            <SectionTitle>👤 Personal Information</SectionTitle>
            <div style={{ 
              background: '#f8f9fa', 
              borderRadius: '12px', 
              padding: '20px',
              lineHeight: '1.6',
              color: '#2f3542'
            }}>
              {(() => {
                const personalInfo = profileData?.personalInfo || competitor.personalInfo;
                if (!personalInfo) return null;
                
                return (
                  <>
                    <p><strong>Age:</strong> {personalInfo.age}</p>
                    <p><strong>Gender:</strong> {personalInfo.gender}</p>
                    <p><strong>Timezone:</strong> {personalInfo.timezone}</p>
                  </>
                );
              })()}
            </div>
          </ProfileSection>
        )}

        {/* Competition Preferences and Challenge Request */}
        <ProfileSection>
          <SectionTitle>🏆 Competition Preferences</SectionTitle>
          <div style={{ 
            background: availabilityStatus.background, 
            borderRadius: '12px', 
            padding: '20px',
            border: `1px solid ${availabilityStatus.color}`,
            marginBottom: '20px'
          }}>
            <div style={{ textAlign: 'center', color: availabilityStatus.color }}>
              <p style={{ margin: '0 0 15px 0', fontWeight: '600' }}>
                {availabilityStatus.text}
              </p>
              {competitor.isOnline && (
                <div style={{ 
                  background: 'linear-gradient(45deg, #4caf50, #8bc34a)',
                  color: 'white',
                  padding: '10px 20px',
                  borderRadius: '20px',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  display: 'inline-block'
                }}>
                  🟢 Ready to Compete!
                </div>
              )}
            </div>
          </div>
          
          {/* Challenge Request Button */}
          <ChallengeRequestButton
            onClick={handleSendChallengeRequest}
            disabled={sendingChallenge || !competitor.isOnline}
          >
            {sendingChallenge ? (
              <>
                <LoadingSpinner />
                Sending Challenge Request...
              </>
            ) : (
              '🎯 Send Challenge Request'
            )}
          </ChallengeRequestButton>
          
          {!competitor.isOnline && (
            <p style={{ 
              textAlign: 'center', 
              color: '#666', 
              fontSize: '0.9rem', 
              marginTop: '10px' 
            }}>
              Challenge requests can only be sent to online competitors
            </p>
          )}
        </ProfileSection>

        <div style={{ padding: '25px', textAlign: 'center' }}>
          <button
            onClick={onClose}
            style={{
              background: 'linear-gradient(45deg, #667eea, #764ba2)',
              color: 'white',
              border: 'none',
              padding: '12px 30px',
              borderRadius: '25px',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.boxShadow = '0 5px 15px rgba(102, 126, 234, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            Close Profile
          </button>
        </div>
      </ModalContent>
    </ModalOverlay>
  );
};

export default ViewProfileModal;
